/**
 * Email log contracts (TRAV-59) — the back-office view over every email the platform has sent.
 * List rows are deliberately light (no body); the rendered HTML is fetched per message via
 * GET /api/email-log/:id/html when an operator opens it.
 */
import { z } from "zod";
import { LANGUAGES } from "./constants";

/** Fixed page size for the ops email-log table. */
export const EMAIL_LOG_PAGE_SIZE = 25;

// ?clubId scopes to one club (the BO club filter); ?page walks newest-first pages.
export const emailLogQuerySchema = z.object({
  clubId: z.uuid().optional(),
  page: z.coerce.number().int().min(1).default(1),
});
export type EmailLogQuery = z.infer<typeof emailLogQuerySchema>;

export interface EmailLogEntryDto {
  id: string;
  /** When the transport accepted the message. */
  createdAt: string;
  clubId: string | null;
  /** Resolved club name, null for back-office recipients / override addresses. */
  clubName: string | null;
  /** Club short code — shown in the compact log table in place of the full name. */
  clubShortCode: string | null;
  recipientEmail: string;
  recipientName: string;
  /** Which email template was rendered (the "type" column). */
  template: string;
  language: (typeof LANGUAGES)[number];
  subject: string;
  /** Which transport delivered it (ses | smtp | resend | log). */
  transport: string;
  /** Folded SES delivery state (TRAV-61); null when the message isn't tracked. */
  deliveryStatus: EmailDeliveryStatus | null;
}

// --- SES delivery tracking (TRAV-61) ---------------------------------------------------------

/**
 * Per-message delivery states, in fold order. Progression states rank upward (a click implies
 * an open implies a delivery); bounced/complained are terminal failures that outrank any
 * engagement signal — a complaint on an opened mail must surface as complained, and AWS
 * penalises unwatched bounce rates, so failures always win the fold.
 */
export const EMAIL_DELIVERY_STATUSES = [
  "sent",
  "delivered",
  "opened",
  "clicked",
  "bounced",
  "complained",
] as const;
export type EmailDeliveryStatus = (typeof EMAIL_DELIVERY_STATUSES)[number];

const DELIVERY_STATUS_RANK: Record<EmailDeliveryStatus, number> = {
  sent: 0,
  delivered: 1,
  opened: 2,
  clicked: 3,
  bounced: 4,
  complained: 5,
};

/**
 * Folds an incoming event's status into the stored one. SNS delivers events at-least-once and
 * out of order, so the fold is a monotonic max over the rank — replaying or reordering events
 * can never regress the stored state (an Open arriving after a Click stays `clicked`).
 */
export function foldDeliveryStatus(
  current: EmailDeliveryStatus | null,
  incoming: EmailDeliveryStatus,
): EmailDeliveryStatus {
  if (current === null) return incoming;
  return DELIVERY_STATUS_RANK[incoming] > DELIVERY_STATUS_RANK[current] ? incoming : current;
}

/**
 * SES event type → delivery status. Covers the configuration-set event destination's tracked
 * types; anything else (Reject, DeliveryDelay, Rendering Failure, Subscription) returns null
 * and is recorded as history without moving the fold.
 */
export function sesEventToStatus(eventType: string): EmailDeliveryStatus | null {
  switch (eventType) {
    case "Send":
      return "sent";
    case "Delivery":
      return "delivered";
    case "Open":
      return "opened";
    case "Click":
      return "clicked";
    case "Bounce":
      return "bounced";
    case "Complaint":
      return "complained";
    default:
      return null;
  }
}

/** One SES event row for the per-message history view (GET /api/email-log/:id/events). */
export interface EmailEventDto {
  id: string;
  /** SES eventType as received (Send | Delivery | Open | Click | Bounce | Complaint | …). */
  eventType: string;
  /** The event's own SES timestamp — not webhook arrival time. */
  occurredAt: string;
}

export interface EmailLogPageDto {
  items: EmailLogEntryDto[];
  /** Total rows matching the filter (for the pager). */
  total: number;
  page: number;
  pageSize: number;
}
